import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Card,
  Form,
  Button,
  Table,
  Row,
  Col,
  Spinner,
  Alert,
  Modal
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { snapshotSettingsAPI, cameraAPI, analysisPromptAPI } from '../utils/api';
import type { Camera } from '../types/camera';
import type { AnalysisPrompt, SnapshotSettings } from '../types/snapshot';

type CameraConfigState = {
  selected: boolean;
  frequency: number;
  retentionDays: number | null;
  promptId: number | null;
};

const SnapshotSettingsPage: React.FC = () => {
  const { currentNursery } = useAuth();
  const { canAccessSnapshotSettings, canEditSnapshotSettings } = useRolePermissions();
  const { t } = useTranslation('pages', { keyPrefix: 'snapshotSettings' });
  const { t: tc } = useTranslation('common');

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [cameras, setCameras] = useState<Camera[]>([]);
  const [prompts, setPrompts] = useState<AnalysisPrompt[]>([]);
  const [settings, setSettings] = useState<SnapshotSettings | null>(null);

  // Form state
  const [enabled, setEnabled] = useState(false);
  const [retentionDays, setRetentionDays] = useState(30);
  const [cameraConfigs, setCameraConfigs] = useState<Record<number, CameraConfigState>>({});
  const [applyAllFrequency, setApplyAllFrequency] = useState(60);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchData = useCallback(async () => {
    if (!currentNursery) return;

    try {
      setLoading(true);
      setError(null);

      const [settingsRes, camerasRes, promptsRes] = await Promise.all([
        snapshotSettingsAPI.getSettings(currentNursery.nursery_id),
        cameraAPI.getCameras(currentNursery.nursery_id),
        analysisPromptAPI.listPrompts(currentNursery.nursery_id)
      ]);

      setCameras(camerasRes.cameras || []);
      setPrompts((promptsRes.data || []).filter(prompt => prompt.active));
      
      const currentSettings = settingsRes.data;
      setSettings(currentSettings);

      if (currentSettings) {
        setEnabled(currentSettings.enabled);
        setRetentionDays(currentSettings.retention_days);
        
        const configs: Record<number, CameraConfigState> = {};
        (camerasRes.cameras || []).forEach(cam => {
          configs[cam.id] = { selected: false, frequency: 60, retentionDays: null, promptId: null };
        });
        
        currentSettings.camera_configs.forEach(conf => {
          if (configs[conf.camera_id]) {
            configs[conf.camera_id] = {
              selected: true,
              frequency: conf.frequency_minutes,
              retentionDays: conf.retention_days ?? null,
              promptId: conf.prompt_id ?? null
            };
          }
        });
        setCameraConfigs(configs);
      } else {
        const configs: Record<number, CameraConfigState> = {};
        (camerasRes.cameras || []).forEach(cam => {
          configs[cam.id] = { selected: false, frequency: 60, retentionDays: null, promptId: null };
        });
        setCameraConfigs(configs);
      }
    } catch (err: any) {
      console.error('Error fetching snapshot settings:', err);
      setError(t('fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [currentNursery, t]);

  useEffect(() => {
    if (canAccessSnapshotSettings) {
      fetchData();
    }
  }, [fetchData, canAccessSnapshotSettings]);

  const handleCameraSelect = (cameraId: number, checked: boolean) => {
    setCameraConfigs(prev => ({
      ...prev,
      [cameraId]: { ...prev[cameraId], selected: checked }
    }));
  };

  const handleFrequencyChange = (cameraId: number, frequency: number) => {
    setCameraConfigs(prev => ({
      ...prev,
      [cameraId]: { ...prev[cameraId], frequency }
    }));
  };

  const handleRetentionChange = (cameraId: number, value: string) => {
    setCameraConfigs(prev => ({
      ...prev,
      [cameraId]: { ...prev[cameraId], retentionDays: value === '' ? null : parseInt(value) }
    }));
  };

  const handlePromptChange = (cameraId: number, value: string) => {
    setCameraConfigs(prev => ({
      ...prev,
      [cameraId]: { ...prev[cameraId], promptId: value ? parseInt(value) : null }
    }));
  };

  const handleApplyAll = () => {
    setCameraConfigs(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(id => {
        const camId = parseInt(id);
        if (next[camId].selected) {
          next[camId].frequency = applyAllFrequency;
        }
      });
      return next;
    });
  };

  const handleSave = async () => {
    if (!currentNursery) return;

    // Validation
    const selectedConfigs = Object.entries(cameraConfigs)
      .filter(([, config]) => config.selected)
      .map(([id, config]) => ({
        camera_id: parseInt(id),
        frequency_minutes: config.frequency,
        retention_days: config.retentionDays ?? undefined,
        prompt_id: config.promptId ?? undefined
      }));

    if (enabled && selectedConfigs.length === 0) {
      setError(t('selectAtLeastOneCamera'));
      return;
    }

    // Frequency validation
    const invalidFrequency = selectedConfigs.some(c => c.frequency_minutes < 5 || c.frequency_minutes > 1440);
    if (invalidFrequency) {
      setError(t('frequencyHint'));
      return;
    }

    const invalidRetention = selectedConfigs.some(c => c.retention_days !== undefined && (c.retention_days < 1 || c.retention_days > 365));
    if (invalidRetention) {
      setError(t('perCameraRetentionValidation'));
      return;
    }

    if (retentionDays < 1 || retentionDays > 365) {
      setError(t('retentionHint'));
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      await snapshotSettingsAPI.updateSettings(currentNursery.nursery_id, {
        enabled,
        retention_days: retentionDays,
        camera_configs: selectedConfigs
      });

      setSuccess(t('saveSuccess'));
      fetchData();
    } catch (err: any) {
      console.error('Error saving snapshot settings:', err);
      setError(t('saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!currentNursery) return;

    try {
      setDeleting(true);
      setError(null);
      await snapshotSettingsAPI.deleteSettings(currentNursery.nursery_id);
      setSuccess(t('deleteSuccess'));
      setShowDeleteModal(false);
      fetchData();
    } catch (err: any) {
      console.error('Error deleting snapshot settings:', err);
      setError(t('deleteFailed'));
    } finally {
      setDeleting(false);
    }
  };

  if (!canAccessSnapshotSettings) {
    return (
      <Layout title={t('title')}>
        <Container className="mt-4">
          <Alert variant="danger">{t('accessDenied')}</Alert>
        </Container>
      </Layout>
    );
  }

  if (loading) {
    return (
      <Layout title={t('title')}>
        <Container className="py-5 text-center">
          <Spinner animation="border" variant="primary" />
        </Container>
      </Layout>
    );
  }

  const groupedCameras = cameras.reduce<Record<string, Camera[]>>((groups, camera) => {
    const classroomName = camera.classroom_name || t('noClassroom');
    groups[classroomName] = groups[classroomName] || [];
    groups[classroomName].push(camera);
    return groups;
  }, {});

  return (
    <Layout title={t('title')}>
      <Container fluid className="px-3">
        <div className="mb-4">
          <h2 className="fw-bold text-primary">{t('title')}</h2>
        </div>

        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

        {!settings && !enabled ? (
           <Card className="text-center p-5 border-0 shadow-sm mb-4">
              <Card.Body>
                <div className="mb-4">
                  <i className="bi bi-camera-fill text-muted" style={{ fontSize: '3rem' }}></i>
                </div>
                <h4>{t('noSettingsYet')}</h4>
                <p className="text-muted mb-4">{t('enabledHint')}</p>
                <Button variant="primary" onClick={() => setEnabled(true)}>
                  {t('configureNow')}
                </Button>
              </Card.Body>
           </Card>
        ) : (
          <Form>
            <Card className="mb-4 shadow-sm border-0">
              <Card.Header className="bg-white py-3 border-0">
                <h5 className="mb-0 fw-bold">{tc('labels.generalSettings')}</h5>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-4">
                      <Form.Check 
                        type="switch"
                        id="snapshot-enabled"
                        label={t('enabled')}
                        checked={enabled}
                        onChange={(e) => setEnabled(e.target.checked)}
                        className="fw-bold"
                        disabled={!canEditSnapshotSettings}
                      />
                      <Form.Text className="text-muted">{t('enabledHint')}</Form.Text>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-4">
                      <Form.Label className="fw-bold">{t('retentionDays')}</Form.Label>
                      <Form.Control 
                        type="number"
                        min={1}
                        max={365}
                        value={retentionDays}
                        onChange={(e) => setRetentionDays(Math.max(1, parseInt(e.target.value) || 30))}
                        disabled={!canEditSnapshotSettings}
                      />
                      <Form.Text className="text-muted">{t('retentionHint')}</Form.Text>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>

            <Card className="mb-4 shadow-sm border-0">
              <Card.Header className="bg-white py-3 border-0 d-flex justify-content-between align-items-center">
                <h5 className="mb-0 fw-bold">{t('cameraConfigs')}</h5>
                {canEditSnapshotSettings && (
                  <div className="d-flex align-items-center gap-2">
                    <Form.Control 
                      type="number"
                      size="sm"
                      style={{ width: '80px' }}
                      value={applyAllFrequency}
                      onChange={(e) => setApplyAllFrequency(Math.max(1, parseInt(e.target.value) || 60))}
                      placeholder="60"
                    />
                    <Button variant="outline-primary" size="sm" onClick={handleApplyAll}>
                      {t('applyToAll')}
                    </Button>
                  </div>
                )}
              </Card.Header>
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th style={{ width: '50px' }}>{t('selected')}</th>
                        <th>{t('camera')}</th>
                        <th>{t('frequencyMinutes')}</th>
                        <th>{t('retentionDays')}</th>
                        <th>{t('prompt')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {cameras.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="text-center py-4">{t('noCamerasAvailable')}</td>
                        </tr>
                      ) : (
                        Object.entries(groupedCameras).map(([classroomName, classroomCameras]) => (
                          <React.Fragment key={classroomName}>
                            <tr className="table-light">
                              <td colSpan={5} className="fw-bold text-muted small">{classroomName}</td>
                            </tr>
                            {classroomCameras.map(camera => (
                              <tr key={camera.id}>
                                <td className="text-center">
                                  <Form.Check 
                                    type="checkbox"
                                    checked={cameraConfigs[camera.id]?.selected || false}
                                    onChange={(e) => handleCameraSelect(camera.id, e.target.checked)}
                                    disabled={!canEditSnapshotSettings}
                                  />
                                </td>
                                <td>{camera.name} <small className="text-muted">({camera.camera_number})</small></td>
                                <td>
                                  <Form.Control 
                                    type="number"
                                    size="sm"
                                    style={{ width: '120px' }}
                                    min={5}
                                    max={1440}
                                    value={cameraConfigs[camera.id]?.frequency || 60}
                                    onChange={(e) => handleFrequencyChange(camera.id, parseInt(e.target.value))}
                                    disabled={!canEditSnapshotSettings || !cameraConfigs[camera.id]?.selected}
                                  />
                                </td>
                                <td>
                                  <div className="d-flex align-items-center gap-2">
                                    <Form.Control 
                                      type="number"
                                      size="sm"
                                      style={{ width: '130px' }}
                                      min={1}
                                      max={365}
                                      value={cameraConfigs[camera.id]?.retentionDays ?? ''}
                                      placeholder={t('retentionPlaceholder', { days: retentionDays })}
                                      onChange={(e) => handleRetentionChange(camera.id, e.target.value)}
                                      disabled={!canEditSnapshotSettings || !cameraConfigs[camera.id]?.selected}
                                    />
                                    <Button
                                      variant="outline-secondary"
                                      size="sm"
                                      onClick={() => handleRetentionChange(camera.id, '')}
                                      disabled={!canEditSnapshotSettings || !cameraConfigs[camera.id]?.selected || cameraConfigs[camera.id]?.retentionDays === null}
                                    >
                                      {t('resetToDefault')}
                                    </Button>
                                  </div>
                                </td>
                                <td>
                                  <Form.Select
                                    size="sm"
                                    value={cameraConfigs[camera.id]?.promptId ?? ''}
                                    onChange={(e) => handlePromptChange(camera.id, e.target.value)}
                                    disabled={!canEditSnapshotSettings || !cameraConfigs[camera.id]?.selected}
                                  >
                                    <option value="">{t('defaultPrompt')}</option>
                                    {prompts.map(prompt => (
                                      <option key={prompt.id} value={prompt.id}>{prompt.name}</option>
                                    ))}
                                  </Form.Select>
                                </td>
                              </tr>
                            ))}
                          </React.Fragment>
                        ))
                      )}
                    </tbody>
                  </Table>
                </div>
                <div className="p-3 border-top">
                  <small className="text-muted">
                    {t('cameraConfigsHint')} {t('perCameraRetentionHint', { days: retentionDays })}
                  </small>
                </div>
              </Card.Body>
            </Card>

            {canEditSnapshotSettings && (
              <div className="d-flex justify-content-between mb-5">
                <Button 
                  variant="outline-danger" 
                  onClick={() => setShowDeleteModal(true)}
                  disabled={saving || deleting || !settings}
                >
                  {t('deleteSettings')}
                </Button>
                <Button 
                  variant="primary" 
                  onClick={handleSave}
                  disabled={saving || deleting}
                  style={{ minWidth: '150px' }}
                >
                  {saving ? <Spinner animation="border" size="sm" /> : t('saveSettings')}
                </Button>
              </div>
            )}
          </Form>
        )}

        <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)} centered>
          <Modal.Header closeButton>
            <Modal.Title>{t('deleteSettings')}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {t('deleteConfirm')}
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
              {tc('buttons.cancel')}
            </Button>
            <Button variant="danger" onClick={handleDelete} disabled={deleting}>
              {deleting ? <Spinner animation="border" size="sm" /> : t('deleteSettings')}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </Layout>
  );
};

export default SnapshotSettingsPage;
